import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';

import { db } from '@/shared/config/firebase';
import {
  OrgaConfiguration,
  OrgaConfigurationSchema
} from '@/models';

/**
 * Repository pour la gestion des configurations d'organisation
 * Permet de récupérer et gérer les informations de chaque organisation
 */
export interface OrgaConfigQueryOptions {
  /** Si true : uniquement les orgas actives (réservé au parcours client public si besoin). */
  activeOnly?: boolean;
}

export class OrgaConfigurationRepository {
  private static readonly COLLECTION_NAME = 'orga-configuration';

  private static buildOrganizationQuery(organizationId: string, activeOnly: boolean) {
    const constraints = [where('organizationId', '==', organizationId)];
    if (activeOnly) {
      constraints.push(where('isActive', '==', true));
    }
    return query(collection(db, this.COLLECTION_NAME), ...constraints);
  }

  /**
   * Récupère la configuration d'une organisation par son organizationId
   */
  static async getByOrganizationId(
    organizationId: string,
    options: OrgaConfigQueryOptions = {}
  ): Promise<OrgaConfiguration | null> {
    const activeOnly = options.activeOnly === true;
    try {
      const q = OrgaConfigurationRepository.buildOrganizationQuery(organizationId, activeOnly);

      try {
        const querySnapshot = await getDocs(q);
        console.log(`📖 [FIRESTORE READ] orga-configuration - getByOrganizationId(${organizationId}) - ${querySnapshot.size} doc(s) lus`);

        if (querySnapshot.empty) {
          return null;
        }

        const doc = querySnapshot.docs[0];
        return OrgaConfigurationRepository.parseConfig(doc.id, doc.data());
      } catch (error: any) {
        console.error(`❌ Erreur lors de la récupération de la configuration pour ${organizationId}:`, error);
        return null;
      }

    } catch (error: any) {
      console.error(`❌ Erreur lors de la requête Firestore pour ${organizationId}:`, error);
      return null;
    }
  }

  /**
   * Écoute les changements en temps réel pour une configuration d'organisation
   */
  static listenToOrganizationConfig(
    organizationId: string,
    callback: (config: OrgaConfiguration | null) => void,
    options: OrgaConfigQueryOptions = {}
  ): () => void {
    const activeOnly = options.activeOnly === true;
    const q = OrgaConfigurationRepository.buildOrganizationQuery(organizationId, activeOnly);

    console.log(`🎧 [FIRESTORE LISTENER] orga-configuration - Création listener pour: ${organizationId} (activeOnly=${activeOnly})`);

    return onSnapshot(q, (querySnapshot) => {
      // Si c'est juste un changement de métadonnées (ex: sync serveur) et que les docs sont identiques, on ignore
      if (querySnapshot.metadata.fromCache === false && querySnapshot.docChanges().length === 0) {
        return;
      }

      const fromCache = querySnapshot.metadata.fromCache;
      const hasPendingWrites = querySnapshot.metadata.hasPendingWrites;

      /* console.log(`📖 [FIRESTORE READ] orga-configuration - Snapshot reçu pour: ${organizationId}`, {
        fromCache,
        hasPendingWrites,
        changes: querySnapshot.docChanges().length
      }); */

      if (querySnapshot.empty) {
        callback(null);
        return;
      }

      const doc = querySnapshot.docs[0];
      const config = OrgaConfigurationRepository.parseConfig(doc.id, doc.data());
      callback(config);
    }, (error) => {
      console.error(`❌ [FIRESTORE ERROR] orga-configuration - listener error:`, error);
      callback(null);
    });
  }

  /**
   * Récupère toutes les configurations d'organisations (actives et inactives)
   */
  static async getAll(): Promise<OrgaConfiguration[]> {
    try {
      const querySnapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      console.log(`📖 [FIRESTORE READ] orga-configuration - getAll() - ${querySnapshot.size} doc(s) lus`);

      const configurations: OrgaConfiguration[] = [];

      querySnapshot.forEach((docSnap) => {
        const config = OrgaConfigurationRepository.parseConfig(docSnap.id, docSnap.data());
        if (config) {
          configurations.push(config);
        }
      });

      return configurations;
    } catch (error: unknown) {
      console.error('Erreur lors de la récupération de toutes les configurations:', error);
      return [];
    }
  }

  /**
   * Récupère toutes les configurations d'organisations actives
   */
  static async getAllActive(): Promise<OrgaConfiguration[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      console.log(`📖 [FIRESTORE READ] orga-configuration - getAllActive() - ${querySnapshot.size} doc(s) lus`);

      const configurations: OrgaConfiguration[] = [];

      querySnapshot.forEach((doc) => {
        const config = OrgaConfigurationRepository.parseConfig(doc.id, doc.data());
        if (config) {
          configurations.push(config);
        }
      });

      return configurations;

    } catch (error: any) {
      console.error('Erreur lors de la récupération des configurations:', error);
      return [];
    }
  }

  /**
   * Crée une nouvelle configuration d'organisation
   */
  static async create(configuration: Omit<OrgaConfiguration, 'id'>): Promise<string> {
    try {
      console.log('📝 Création d\'une nouvelle configuration d\'organisation:', configuration.organizationId);

      // Déterminer le plan par défaut en fonction de la période
      const LAUNCH_OFFER_END_DATE = new Date('2026-05-30T23:59:59');
      const now = new Date();
      const isLaunchOfferPeriod = now <= LAUNCH_OFFER_END_DATE;

      // Préparer la configuration d'abonnement
      const subscriptionConfig: any = configuration.subscriptionConfig || {
        planType: isLaunchOfferPeriod ? 'LAUNCH_OFFER' : 'FREEMIUM',
        welcomeBonusAmount: 0,
        welcomeBonusUsed: 0,
        iaConfig: {
          enabled: false,
          creditsRemaining: 0,
          creditsUsed: 0,
        },
        savConfig: {
          chatEnabled: false,
          historyEnabled: false,
        },
      };

      // Si la config existe déjà mais n'a pas de planType, l'ajouter
      if (!subscriptionConfig.planType) {
        subscriptionConfig.planType = isLaunchOfferPeriod ? 'LAUNCH_OFFER' : 'FREEMIUM';
      }

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
        ...configuration,
        subscriptionConfig,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      console.log(`✅ Configuration créée avec l'ID: ${docRef.id}`);
      console.log(`📋 Plan attribué: ${subscriptionConfig.planType}`);
      return docRef.id;

    } catch (error: any) {
      console.error('❌ Erreur lors de la création de la configuration:', error);
      throw new Error('Échec de la création de la configuration');
    }
  }

  /**
   * Nettoie récursivement les valeurs undefined d'un objet
   * Firestore ne peut pas stocker de valeurs undefined
   */
  private static cleanUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanUndefinedValues(item));
    }

    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.cleanUndefinedValues(value);
        }
      }
      return cleaned;
    }

    return obj;
  }

  /**
   * Met à jour une configuration d'organisation
   */
  static async update(organizationId: string, updates: Partial<OrgaConfiguration>): Promise<void> {
    try {
      console.log(`📝 Mise à jour de la configuration pour: ${organizationId}`);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('organizationId', '==', organizationId)
      );

      const querySnapshot = await getDocs(q);
      console.log(`📖 [FIRESTORE READ] orga-configuration - update(${organizationId}) - ${querySnapshot.size} doc(s) lus`);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;

        // Nettoyer récursivement les valeurs undefined avant la mise à jour
        const cleanUpdates = this.cleanUndefinedValues(updates);

        // Supprimer les champs complètement vides
        Object.keys(cleanUpdates).forEach(key => {
          if (cleanUpdates[key] === null || (typeof cleanUpdates[key] === 'object' && Object.keys(cleanUpdates[key]).length === 0)) {
            delete cleanUpdates[key];
          }
        });

        await updateDoc(docRef, {
          ...cleanUpdates,
          updatedAt: serverTimestamp(),
        });

        console.log(`✅ Configuration mise à jour pour ${organizationId}`);
      } else {
        console.log(`⚠️ Aucune configuration trouvée pour ${organizationId}`);
      }

    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour de la configuration pour ${organizationId}:`, error);
      throw new Error('Échec de la mise à jour de la configuration');
    }
  }

  /**
   * Met à jour les informations de contact d'une organisation
   */
  static async updateContactInfo(
    organizationId: string,
    contactInfo: {
      address?: {
        street?: string;
        city?: string;
        postalCode?: string;
        country?: string;
      };
      phone?: string;
      email?: string;
    }
  ): Promise<void> {
    try {
      console.log(`📞 Mise à jour des informations de contact pour: ${organizationId}`);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('organizationId', '==', organizationId)
      );

      const querySnapshot = await getDocs(q);
      console.log(`📖 [FIRESTORE READ] orga-configuration - updateContactInfo(${organizationId}) - ${querySnapshot.size} doc(s) lus`);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        const updates: any = {
          updatedAt: serverTimestamp(),
        };

        // Ajouter les champs de contact s'ils sont fournis
        if (contactInfo.address) {
          updates.address = contactInfo.address;
          // Synchroniser avec legalInfo.address
          updates['legalInfo.address'] = contactInfo.address;
        }
        if (contactInfo.phone !== undefined) {
          updates.phone = contactInfo.phone;
        }
        if (contactInfo.email !== undefined) {
          updates.email = contactInfo.email;
        }

        await updateDoc(docRef, updates);

        console.log(`✅ Informations de contact mises à jour pour ${organizationId}`);
      } else {
        console.log(`⚠️ Aucune configuration trouvée pour ${organizationId}`);
      }

    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour des informations de contact pour ${organizationId}:`, error);
      throw new Error('Échec de la mise à jour des informations de contact');
    }
  }

  /**
   * Active ou désactive une organisation
   */
  static async toggleOrganizationStatus(
    organizationId: string,
    isActive: boolean
  ): Promise<void> {
    try {
      console.log(`${isActive ? '🟢' : '🔴'} ${isActive ? 'Activation' : 'Désactivation'} de l'organisation: ${organizationId}`);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('organizationId', '==', organizationId)
      );

      const querySnapshot = await getDocs(q);
      console.log(`📖 [FIRESTORE READ] orga-configuration - toggleOrganizationStatus(${organizationId}) - ${querySnapshot.size} doc(s) lus`);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          isActive,
          updatedAt: serverTimestamp(),
        });

        console.log(`✅ Organisation ${isActive ? 'activée' : 'désactivée'} pour ${organizationId}`);
      } else {
        console.log(`⚠️ Aucune configuration trouvée pour ${organizationId}`);
      }

    } catch (error) {
      console.error(`❌ Erreur lors de la ${isActive ? 'activation' : 'désactivation'} de l'organisation ${organizationId}:`, error);
      throw new Error(`Échec de la ${isActive ? 'activation' : 'désactivation'} de l'organisation`);
    }
  }

  /**
   * Met à jour la configuration multi-provider shipping d'une organisation
   */
  static async updateShippingConfig(
    organizationId: string,
    shippingConfig: import('@/models').ShippingConfiguration
  ): Promise<void> {
    const q = query(collection(db, this.COLLECTION_NAME), where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, {
        shippingConfig: this.cleanUndefinedValues(shippingConfig as any),
        updatedAt: serverTimestamp(),
      });
    }
  }

  /**
   * Parse une configuration brute avec fallback en cas d'échec de validation Zod
   */
  private static parseConfig(id: string, rawData: any): OrgaConfiguration | null {
    if (!rawData) return null;

    const configData = {
      id,
      ...OrgaConfigurationRepository.convertTimestamps(rawData)
    };

    // Debug: Log payment-related fields from raw data
    // Debug: Log payment-related fields from raw data
    /* console.log('🔍 parseConfig - Payment fields debug:', {
      id,
      rawPaymentMode: rawData.paymentMode,
      configDataPaymentMode: configData.paymentMode,
      hasStripeApiKeyConfig: !!rawData.stripeApiKeyConfig,
      stripeApiKeyHasSecretKey: rawData.stripeApiKeyConfig?.hasSecretKey,
      stripeConfigPaymentMode: rawData.stripeConfig?.paymentMode
    }); */

    // Try Zod parsing
    try {
      const parsed = OrgaConfigurationSchema.parse(configData);
      // console.log('✅ parseConfig - Zod OK for', id, '- paymentMode:', parsed.paymentMode, '- stripeApiKeyConfig:', !!parsed.stripeApiKeyConfig);
      return parsed;
    } catch (parseError) {
      console.warn(`⚠️ Zod FAILED for ${id}:`, parseError);

      // Fallback robuste pour éviter de bloquer l'application
      const fallbackConfig = {
        id,
        organizationId: configData.organizationId || '',
        name: configData.name || 'Organisation',
        description: configData.description || '',
        logo: configData.logo || '',
        isActive: configData.isActive ?? true,
        stripeConfig: configData.stripeConfig || {},
        sendCloudConfig: configData.sendCloudConfig || {},
        subscriptionConfig: configData.subscriptionConfig || {},
        stripeConnectConfig: configData.stripeConnectConfig,
        stripeApiKeyConfig: configData.stripeApiKeyConfig,
        paypalConfig: configData.paypalConfig,
        commissionConfig: configData.commissionConfig,
        creditsBalance: configData.creditsBalance,
        createdAt: configData.createdAt instanceof Date ? configData.createdAt : new Date(),
        updatedAt: configData.updatedAt instanceof Date ? configData.updatedAt : new Date(),
        address: configData.address,
        legalInfo: configData.legalInfo,
        contacts: configData.contacts || [],
        afterSalesService: {
          isSavChatEnabled: configData.afterSalesService?.isSavChatEnabled ?? false,
          contacts: configData.afterSalesService?.contacts || [],
          description: configData.afterSalesService?.description
        },
        socials: configData.socials || {},
        isStripeEnabled: configData.isStripeEnabled ?? false,
        paymentMode: configData.paymentMode || configData.stripeConfig?.paymentMode || 'none'
      } as unknown as OrgaConfiguration;

      console.log('🔧 parseConfig - Fallback for', id, '- paymentMode:', fallbackConfig.paymentMode);

      return fallbackConfig;
    }
  }

  /**
   * Convertit récursivement les Timestamps Firestore en Dates JS
   */
  private static convertTimestamps(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Gestion du cas Timestamp Firestore (objet avec toDate())
    if (obj && typeof obj.toDate === 'function') {
      return obj.toDate();
    }

    // Gestion du cas Timestamp sérialisé (objet avec seconds/nanoseconds)
    if (obj && typeof obj === 'object' && ('seconds' in obj || '_seconds' in obj)) {
      const seconds = obj.seconds || obj._seconds;
      return new Date(seconds * 1000);
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.convertTimestamps(item));
    }

    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = this.convertTimestamps(value);
      }
      return result;
    }

    return obj;
  }
}
